import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { accessApi } from "./api";
import { CODE_LENGTH, PASSWORD_PROBLEM_KEY, isCodeComplete, normalizeCode, passwordProblem } from "./codes";
import { readFailure } from "./failure";
import { PublicHeading } from "./public-layout";

/**
 * Password reset by a code to the mail address ([F-029], [AC-053]).
 *
 * The request answers the same whether or not the address has an account -
 * the screen therefore always moves on to the code step and says "if an
 * account exists". The confirmation sets the new password and ends every
 * session of the account. The system account's password lives in the
 * deployment, and the server says so in words ([F-031]).
 */
export function ResetPage(): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = useState<"request" | "confirm" | "done">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pwProblem = password === "" ? null : passwordProblem(password, repeat === "" ? undefined : repeat);
  const ready = isCodeComplete(code) && repeat !== "" && passwordProblem(password, repeat) === null;

  const request = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await accessApi.resetRequest(email.trim());
      setStep("confirm");
    } catch (failure) {
      setError(readFailure(failure, t("reset.failed")).message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await accessApi.resetConfirm({ email: email.trim(), code: normalizeCode(code), password });
      setStep("done");
    } catch (failure) {
      setError(readFailure(failure, t("reset.failed")).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="reset_page">
      <PublicHeading
        testid="reset_title"
        title={t("reset.title")}
        lead={step === "request" ? t("reset.lead") : step === "confirm" ? t("reset.sent", { email: email.trim() }) : undefined}
      />

      {step === "done" ? (
        <>
          <p data-testid="reset_done" role="status" className="text-sm">
            {t("reset.done")}
          </p>
          <Button asChild className="mt-6 min-h-11 w-full">
            <Link to="/anmelden" data-testid="reset_to-login">
              {t("reset.toLogin")}
            </Link>
          </Button>
        </>
      ) : step === "request" ? (
        <form key="request" data-testid="reset_request-form" className="space-y-4" onSubmit={(event) => void request(event)}>
          <div className="space-y-2">
            <Label htmlFor="reset-email">{t("login.email")}</Label>
            <Input
              id="reset-email"
              data-testid="reset_email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <Button data-testid="reset_request" type="submit" className="min-h-11 w-full" disabled={busy}>
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {t("reset.request")}
          </Button>
        </form>
      ) : (
        <form key="confirm" data-testid="reset_confirm-form" className="space-y-4" onSubmit={(event) => void confirm(event)}>
          <div className="space-y-2">
            <Label htmlFor="reset-code">{t("reset.code")}</Label>
            <Input
              id="reset-code"
              data-testid="reset_code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              required
              maxLength={CODE_LENGTH + 2}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-password">{t("reset.password")}</Label>
            <Input
              id="reset-password"
              data-testid="reset_password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <p className="text-xs text-muted-foreground">{t("forms.password.hint")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reset-repeat">{t("reset.passwordRepeat")}</Label>
            <Input
              id="reset-repeat"
              data-testid="reset_password-repeat"
              type="password"
              autoComplete="new-password"
              required
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
            />
            {pwProblem ? (
              <p data-testid="reset_password-problem" className="text-xs text-destructive-text">
                {t(PASSWORD_PROBLEM_KEY[pwProblem])}
              </p>
            ) : null}
          </div>
          <Button data-testid="reset_confirm" type="submit" className="min-h-11 w-full" disabled={busy || !ready}>
            {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {t("reset.confirm")}
          </Button>
          <Button
            data-testid="reset_again"
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep("request");
              setCode("");
              setError(null);
            }}
          >
            {t("reset.again")}
          </Button>
        </form>
      )}

      {error ? (
        <p data-testid="reset_error" role="alert" className="mt-4 text-sm text-destructive-text">
          {error}
        </p>
      ) : null}

      {step !== "done" ? (
        <Link to="/anmelden" data-testid="reset_back" className="mt-6 block text-sm text-muted-foreground hover:text-foreground">
          {t("reset.back")}
        </Link>
      ) : null}
    </section>
  );
}
